import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import DeviceService from '../services/DeviceService';

/**
 * Contexto de estado de dispositivos
 * Centraliza datos de BD + WebSocket en un solo lugar.
 * Todos los componentes que necesiten datos de dispositivos
 * deben usar useDeviceStatus() en lugar de useWebSocket() directamente.
 */

const DeviceStatusContext = createContext();

export const DeviceStatusProvider = ({ children }) => {
  const [devices, setDevices] = useState([]);
  const [activeDevices, setActiveDevices] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Única instancia de WebSocket — gestionada aquí
  const { devices: wsDevices, isConnected, stats: wsStats, onDeviceEvent, connectionLog, sensorAlerts, telemetryIntervals } = useWebSocket();

  /**
   * Carga todos los dispositivos desde la base de datos
   */
  const fetchAllDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const fetchedDevices = await DeviceService.getAllDevices();
      setDevices(fetchedDevices);

      // Populate initial activeDevices from API isOnline field
      setActiveDevices(prev => {
        const merged = new Map(prev);
        fetchedDevices.forEach(device => {
          if (device.mac && device.isOnline && !merged.has(device.mac)) {
            merged.set(device.mac, {
              mac: device.mac,
              isActive: true,
              lastSeen: device.lastSeen || new Date().toISOString(),
              ...(device.liveData || {})
            });
          }
        });
        return merged;
      });

      return fetchedDevices;
    } catch (err) {
      const errorMsg = err.message || 'Error al cargar dispositivos';
      setError(errorMsg);
      console.error('Error al cargar dispositivos:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualiza dispositivos activos desde WebSocket
   */
  useEffect(() => {
    if (!wsDevices || wsDevices.length === 0) return;

    setActiveDevices(prev => {
      const activeMap = new Map(prev);
      wsDevices.forEach(wsDevice => {
        if (wsDevice.mac) {
          const existing = activeMap.get(wsDevice.mac) || {};
          activeMap.set(wsDevice.mac, {
            ...existing,
            ...wsDevice,
            isActive: true,
            lastSeen: wsDevice.lastSeen || new Date().toISOString(),
            // sensorName es propiedad del hardware — preservar el valor del initial load
            // si el mensaje WebSocket no lo trae (mensajes lv=1 no persisten este dato)
            sensorName: wsDevice.sensorName ?? existing.sensorName ?? null,
          });
        }
      });
      return activeMap;
    });
  }, [wsDevices]);

  // NO auto-fetch on mount — each page that needs `devices` calls
  // fetchAllDevices() explicitly. This avoids a heavy redundant
  // /admin/firmware/versions call on pages like Overview that have
  // their own optimised endpoint.

  /**
   * Obtiene información completa de un dispositivo (BD + status en tiempo real)
   */
  const getDeviceInfo = useCallback((mac) => {
    const dbDevice = devices.find(d => d.mac === mac);
    if (!dbDevice) return null;

    return DeviceService.mergeDeviceWithLiveStatus(dbDevice, activeDevices);
  }, [devices, activeDevices]);

  /**
   * Obtiene todos los dispositivos con su status actualizado
   */
  const getAllDevicesWithStatus = useCallback(() => {
    return devices.map(device => 
      DeviceService.mergeDeviceWithLiveStatus(device, activeDevices)
    );
  }, [devices, activeDevices]);

  /**
   * Verifica si un dispositivo está activo
   */
  const isDeviceActive = useCallback((mac) => {
    return activeDevices.has(mac);
  }, [activeDevices]);

  /**
   * Filtra dispositivos según criterios
   */
  const filterDevices = useCallback((filters) => {
    const devicesWithStatus = getAllDevicesWithStatus();
    return DeviceService.filterDevices(devicesWithStatus, filters);
  }, [getAllDevicesWithStatus]);

  /**
   * Ordena dispositivos
   */
  const sortDevices = useCallback((field, direction = 'asc') => {
    const devicesWithStatus = getAllDevicesWithStatus();
    return DeviceService.sortDevices(devicesWithStatus, field, direction);
  }, [getAllDevicesWithStatus]);

  const value = useMemo(() => ({
    devices,
    activeDevices,
    wsDevices,
    wsStats,
    loading,
    error,
    getDeviceInfo,
    getAllDevicesWithStatus,
    fetchAllDevices,
    isDeviceActive,
    filterDevices,
    sortDevices,
    onDeviceEvent,
    connectionLog,
    sensorAlerts,
    telemetryIntervals,
    wsConnected: isConnected,
  }), [devices, activeDevices, wsDevices, wsStats, loading, error, getDeviceInfo, getAllDevicesWithStatus, fetchAllDevices, isDeviceActive, filterDevices, sortDevices, onDeviceEvent, connectionLog, sensorAlerts, telemetryIntervals, isConnected]);

  return (
    <DeviceStatusContext.Provider value={value}>
      {children}
    </DeviceStatusContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de dispositivos
 * @throws {Error} Si se usa fuera del DeviceStatusProvider
 */
export const useDeviceStatus = () => {
  const context = useContext(DeviceStatusContext);
  if (!context) {
    throw new Error('useDeviceStatus debe usarse dentro de DeviceStatusProvider');
  }
  return context;
};
